import { useCallback } from "react"
import { SWITCH_STEP, REVEAL_QUALIFIER_RADIO, RESTART } from "../wizard"
import {
  ISharedContext,
  ViewModes,
  validateForm,
  requiresQualification,
} from "../support/registration_form_component"
import { getQueryParams } from "../support/helpers"

export interface ISidebarProps extends ISharedContext {
  submit: () => void
  stepNames: readonly string[]
}
export function RegistrationSidebar({
  current_step,
  max_step,
  stepNames,
  editable,
  view_mode,
  urls,
  client,
  form,
  submit,
  dispatch,
}: ISidebarProps) {

  let onQualificationStep = false
  if (requiresQualification(client, form) &&
    current_step === 2 &&
    document.getElementsByClassName("qualification-confirmation-or-rejection").length > 0) {
    onQualificationStep = true
  }

  const ignoreValidations = getQueryParams()["validate"] === "0"

  /*
   * Prevent going forward if form is not valid, otherwise send a msg to the wizard
   * to switch to the step.
   */
  const switchStep = useCallback((stepNum: number | null | undefined) => {
    if (typeof stepNum === "undefined" || stepNum === null || stepNum === current_step) {
      return
    }

    const goingBack = stepNum < current_step

    if (goingBack && onQualificationStep) {
      // go back to the qualification answer page instead
      return dispatch({ action: REVEAL_QUALIFIER_RADIO })
    }

    if (ignoreValidations || goingBack || validateForm()) {
      dispatch({
        action: SWITCH_STEP,
        step: stepNum,
      })
    }

    // console.warn("not switching step")
  }, [current_step, onQualificationStep, ignoreValidations, dispatch])

  const onStartOver = useCallback(() => {
    dispatch({ action: RESTART })
  }, [dispatch])

  const stepNamesWithQualifier = stepNames.slice()
  if (requiresQualification(client, form)) {
    stepNamesWithQualifier.splice(1, 0, "Qualifier")
  }

  const numberOfSteps = stepNamesWithQualifier.length

  // max_step - 1 = we aren't done yet with the step
  // number_of_steps - 1 = the last step "Review and Submit" isn't really a step
  let progressPercentage = Math.ceil(((max_step - 1) / (numberOfSteps - 1)) * 100)
  if (progressPercentage > 100.0) {
    progressPercentage = 100
  }
  const percentageWidth = `${progressPercentage}%`

  let previousStep: number | null = current_step - 1
  let nextStep: number | null = current_step + 1
  const isLastStep = current_step === numberOfSteps

  if (previousStep < 0) {
    previousStep = null
  }

  if (current_step > numberOfSteps) {
    nextStep = null
  }

  const onNextStep = useCallback(() => switchStep(nextStep), [switchStep, nextStep])

  if (view_mode === ViewModes.DOWNLOADABLE) {
    return <div />
  }

  // don't show on the intro step or the submission step
  if (current_step === 0 || current_step > numberOfSteps) {
    return <div />
  }

  let stepClassName = ""
  if (numberOfSteps === 5) {
    stepClassName = "five-step"
  } else if (numberOfSteps === 4) {
    stepClassName = "four-step"
  }

  const sharedStepProps = {
    current_step: current_step,
    max_step: max_step,
    switchStep: switchStep,
  }

  return (
    <div className="progress-indicator hidden-print" id="page-nav">
      {!editable && <StartOverButton onClick={onStartOver} /> }
      {editable &&
          <PreviousButton
            previousStep={previousStep}
            switchStep={switchStep}
          />
      }

      {!isLastStep && <NextStepButton onClick={onNextStep} />}
      {isLastStep && editable && <FinishAndSubmitButton onClick={submit} />}
      {isLastStep && !editable && <PrintButton url={urls["print"]} />}

      <div className={`progress-indicator__steps ${stepClassName}`}>
        {stepNamesWithQualifier.map((stepName, index) => {
          const num = index + 1
          return (
            <NavStep
              key={num}
              number={num}
              name={stepName}
              {...sharedStepProps}
            />
          )
        })}

        <div className="progress">
          <div
            className="progress-bar progress-bar-success"
            style={{ width: percentageWidth }}
            id="progress-bar"
            role="progressbar"
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
    </div>
  )
}

interface INavStepProps {
  name: string
  number: number
  current_step: number
  max_step: number
  switchStep: (step: number) => void
}
function NavStep({
  name,
  max_step,
  current_step,
  number,
  switchStep,
}: INavStepProps) {
  const onClick = useCallback(() => {
    switchStep(number)
  }, [switchStep, number])

  const classNames: string[] = []

  // special exception for thank you page
  // TODO: 4 may not always be thank you page
  const active = number === current_step || (number === 3 && current_step === 4)

  const complete = number < max_step
  const enabled = number <= max_step

  classNames.push("progress-indicator__step")
  classNames.push(active ? "active" : "inactive")
  classNames.push(enabled ? "enabled" : "disabled")
  classNames.push(complete ? "complete" : "incomplete")
  classNames.push(`nav-step-${number}`)

  const listItemClassNames = classNames.join(" ")

  return (
    <div id={`nav-step-${number}`} className={listItemClassNames}>
      <a onClick={onClick}>
        <span className="progress-indicator__number">
          <i className="fa fa-check progress-indicator__check" />
          <span className="progress-indicator__number-label">{number}</span>
        </span>
        <span className="progress-indicator__label">{name}</span>
      </a>
    </div>
  )
}

function PreviousButton({
  previousStep,
  switchStep,
}: {
  previousStep: number | null
  switchStep: (step: number | null) => void
}) {
  const onClick = useCallback(() => {
    switchStep(previousStep)
  }, [switchStep, previousStep])

  if (previousStep === null) {
    return null
  }

  return (
    <a className="btn btn-info btn--left" role="button" onClick={onClick}>
      <i className="fa fa-angle-left" />
        &nbsp; Previous Step
    </a>
  )
}

interface IStartOverButtonProps {
  onClick: () => void
}
function StartOverButton({ onClick }: IStartOverButtonProps) {
  const clickHandler = useCallback(() => {
    if (confirm("Are you sure?")) {
      onClick()
    }
  }, [onClick])
  return (
    <a
      className="btn btn-info btn--left"
      role="button"
      onClick={clickHandler}
    >
      Start Over
    </a>
  )
}

interface IPrintButtonProps {
  url: string
}
function PrintButton({ url }: IPrintButtonProps) {
  return (
    <a
      className="btn btn-info btn-success btn--right finish-and-submit-form"
      href={url}
    >
      Print Form &nbsp;
      <i className="fa fa-angle-right" />
    </a>
  )
}

interface IFinishAndSubmitButtonProps {
  onClick: () => void
}
function FinishAndSubmitButton({ onClick }: IFinishAndSubmitButtonProps) {
  return (
    <a
      className="btn btn-success btn--right btn--pulse finish-and-submit-form"
      role="button"
      onClick={onClick}
    >
      Finish &amp; Submit Form &nbsp;
      <i className="fa fa-angle-right" />
    </a>
  )
}

interface INextStepButtonProps {
  onClick: () => void
}
function NextStepButton({ onClick }: INextStepButtonProps) {
  return (
    <a className="btn btn-info btn--right next-step" role="button" onClick={onClick}>
      Next Step &nbsp;
      <i className="fa fa-angle-right" />
    </a>
  )
}
